import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Button } from '@/components/design-system';
import { AddCardForm } from '@/components/commons/CardForm';
import { Card } from '@/models/card';
import { useCards } from '@/stores/cards/store';
import CardCheckoutDisplay from './CardCheckoutDisplay';
import { Spinner } from '@/components/commons/Spinner';

export interface IProps {
  card: Card;
  setCheckoutCard: (card: Card, paymentMethodId?: string) => void;
  resetCheckoutCard: () => void;
  isListVisible: boolean;
  setIsListVisible: (isListVisible: boolean) => void;
}

export default function CheckoutPaymentSelection(props: IProps) {
  const [toggleForm, setToggleForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const t = useTranslations('checkout');
  const { cards, fetchCards } = useCards();

  useEffect(() => {
    // Se è già stata selezionata una carta
    if (props.card.token) {
      props.setIsListVisible(false);
    }
    // Se non ci sono carte
    if (cards.length === 0) {
      props.setIsListVisible(false);
      onAddCard();
    }
  }, []);

  const onAddCard = async () => {
    props.setIsListVisible(false);
    props.resetCheckoutCard();
    setToggleForm(true);
  };

  const onResponse = (paymentMethodId: any): void => {
    setIsLoading(true);
    setToggleForm(false);
    fetchCards()
      .then((fetchedCards) => {
        props.setCheckoutCard(
          fetchedCards!.find((c) => c.token === paymentMethodId)!,
          paymentMethodId,
        );
      })
      .catch(() => { return; })
      .finally(() => {
        setIsLoading(false);
        props.setIsListVisible(false);
      });
  };

  const onCardClicked = (card: Card): void => {
    props.setIsListVisible(false);
    setToggleForm(false);
    props.setCheckoutCard(card, card.token);
  };

  const renderCreditCard = (): any => {
    if (isLoading) {
      return (
        <div className="tw-text-center">
          {' '}
          <Spinner size='small' className="tw-my-3"/>
        </div>
      );
    }

    if (props.card.token && !toggleForm) {
      return !isLoading ? (
        <CardCheckoutDisplay isSelectable={false} card={props.card} />
      ) : (
        <div className="tw-text-center">
          {' '}
          <Spinner size='small' className="tw-my-3"/>
        </div>
      );
    } else {
      return (
        <div>
          <AddCardForm
            onSuccess={(si) => onResponse(si.setupIntent!.payment_method)}
          />
          <div className="tw-mt-4">
            <em className="tw-self-center">
              {t('CheckoutPaymentSelection.payment-info-will-be-saved')}
            </em>
          </div>
        </div>
      );
    }
  };

  return (
    <>
      <div className="tw-pt-3">
        <div className="tw-flex tw-justify-between">
          {cards.length !== 0 && (
            <div>
              {(props.card.token || !props.isListVisible) && (
                <a onClick={() => props.setIsListVisible(!props.isListVisible)}>
                  {props.isListVisible
                    ? t('CheckoutPaymentSelection.cancel')
                    : t('CheckoutPaymentSelection.change')}
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="tw-mb-2 tw-mt-3">
        {!props.isListVisible ? (
          <>{renderCreditCard()}</>
        ) : (
          <>
            {cards.map((card, idx) => {
              return (
                <CardCheckoutDisplay
                  key={idx}
                  isSelectable={true}
                  onCardClicked={onCardClicked}
                  card={card}
                />
              );
            })}
            <Button
              className="tw-mt-2"
              onClick={onAddCard}>
              {t('CheckoutPaymentSelection.insert-new-card')}
            </Button>
          </>
        )}
      </div>
    </>
  );
}
